// src/hooks/useUswdSocket.js
import {
  useEffect,
  useCallback,
  useRef,
} from "react";

/**
 * useUswdSocket
 *
 * Mirrors the two server events from uswdSocket.js:
 *
 *   "uswd-transfer-result"  { success, data?, error }
 *     data.wallet  = { balance, committed, available }  ← post-update DB values
 *     data.ledger  = { id, txHash, status }
 *     data.amount, data.recipientAddress
 *     NOTE: data.transaction no longer exists (USWDTransaction removed server-side)
 *
 *   "uswd-balance-updated"  { wallet: { balance, committed, available }, amount, from, txHash }
 *     ← credit notification sent to recipient; wallet object IS included now.
 *     The hook applies the wallet object directly and notifies the parent
 *     via onBalanceUpdated so it can optionally trigger a full refreshUswdBalance().
 *
 * NOTE: wallets are now custodial (server-held keys). The client no longer
 * loads a signing key or signs the transfer payload — the server decrypts
 * its own stored key material as the authorization step (see
 * uswdSocket.js / decodeSigningKey). transferUSWD() just emits
 * { recipientAddress, amount, timestamp }.
 */
const useUswdSocket = ({

  socket,

  setUswdWallet,

  onTransferResult,

  onBalanceUpdated,

  setUswdHistory,

  onHistory,

}) => {
  /* Pending resolve/reject for the in-flight transfer promise */
  const pendingTransferRef = useRef(null);

  useEffect(() => {
    if (!socket) return;

    /* ── uswd-transfer-result ── */
    const handleTransferResult = (payload) => {
  

      /* Settle the in-flight promise from transferUSWD() */
      if (pendingTransferRef.current) {
        /* Clear the safety timeout before settling */
        clearTimeout(pendingTransferRef.current._timeout);

        if (payload?.success) {
          pendingTransferRef.current.resolve(payload);
        } else {
          pendingTransferRef.current.reject(payload);
        }
        pendingTransferRef.current = null;
      }

      /*
       * Update wallet from the server response.
       * uswdSocket.js returns wallet.available straight from the
       * DB document (new: true), so this is always accurate.
       *
       * Shape: payload.data.wallet = { balance, committed, available }
       */
      if (payload?.success && payload?.data?.wallet) {
        setUswdWallet?.((prev) => ({
          ...(prev ?? {}),
          ...payload.data.wallet,
        }));
      }

      onTransferResult?.(payload);
    };

    /*
     * ── uswd-balance-updated ──
     *
     * Server shape (from uswdSocket.js):
     *   io.to(recipientUserRoom).emit("uswd-balance-updated", {
     *     wallet: { balance, committed, available },
     *     amount,
     *     from,
     *     txHash,
     *   });
     *
     * The wallet object IS present — apply it directly so the
     * recipient's balance updates immediately without a round-trip.
     * Then notify the parent via onBalanceUpdated so it can optionally
     * trigger a full refreshUswdBalance() for a server-confirmed figure.
     */
    const handleBalanceUpdated = (payload) => {

      if (payload?.wallet) {
        setUswdWallet?.((prev) => ({
          ...(prev ?? {}),
          ...payload.wallet,
        }));
      }

      /*
       * Notify parent — passes the raw server payload:
       *   { wallet, amount, from, txHash }
       */
      onBalanceUpdated?.(payload);
    };

    /*
     * ── uswd-history ──
     *
     * Server shape:
     * {
     *   history: {
     *     txHash,
     *     amount,
     *     timestamp,
     *     direction,
     *     counterparty,
     *     status,
     *     fee,
     *   }
     * }
     */
const handleHistory = (payload) => {

  console.log(
    "[USWD HISTORY] Received:",
    payload
  );

  if (!payload?.history) {

    console.log(
      "[USWD HISTORY] Missing history property"
    );

    return;

  }

  console.log(
    "[USWD HISTORY] Adding history:",
    payload.history
  );

  setUswdHistory?.((previous) => [

    payload.history,

    ...(previous || []),

  ]);

  onHistory?.(
    payload.history
  );

};

    socket.on(
      "uswd-transfer-result",
      handleTransferResult
    );

    socket.on(
      "uswd-balance-updated",
      handleBalanceUpdated
    );

    socket.on(
      "uswd-history",
      handleHistory
    );

    return () => {
      socket.off(
        "uswd-transfer-result",
        handleTransferResult
      );

      socket.off(
        "uswd-balance-updated",
        handleBalanceUpdated
      );

      socket.off(
        "uswd-history",
        handleHistory
      );
    };
  }, [
    socket,

    onTransferResult,

    onBalanceUpdated,

    setUswdWallet,

    setUswdHistory,

    onHistory,
  ]);

  /**
   * transferUSWD
   *
   * Emits "uswd-transfer" and returns a Promise that resolves/rejects
   * when "uswd-transfer-result" comes back.
   *
   * Wallets are custodial now — no client-side key loading or signing.
   * The server authorizes the transfer by decrypting its own stored
   * key material for the sender's wallet.
   *
   * @param {{ recipientAddress: string, amount: number }} params
   * @returns {Promise<{
   *   success: true,
   *   data: {
   *     amount:           number,
   *     recipientAddress: string,
   *     ledger:           { id: string, txHash: string, status: string },
   *     wallet:           { balance: number, committed: number, available: number },
   *   }
   * }>}
   */
  const transferUSWD =
  useCallback(

  ({ recipientAddress, amount }) =>

  new Promise(

  (
  resolve,
  reject
  ) => {

    try {

      if (!socket) {

        reject({
          error:
          "Socket not connected"
        });

        return;

      }

      /* ──────────────────────
         BUILD PAYLOAD
      ────────────────────── */

      const timestamp =
      Date.now();

      /* ──────────────────────
         OVERWRITE PREVIOUS
      ────────────────────── */

      if (
        pendingTransferRef.current
      ) {

        clearTimeout(
          pendingTransferRef
          .current
          ._timeout
        );

        pendingTransferRef
        .current
        .reject({

          error:
          "Superseded by new transfer"

        });

      }

      pendingTransferRef.current = {

        resolve,
        reject,

      };

      socket.emit(

        "uswd-transfer",

        {

          recipientAddress,

          amount,

          timestamp,

        }

      );
      const timeout =
      setTimeout(() => {

        if (
          pendingTransferRef.current
        ) {

          pendingTransferRef
          .current
          .reject({

            error:
            "Transfer timed out"

          });

          pendingTransferRef.current =
          null;

        }

      },15000);

      pendingTransferRef.current._timeout =
      timeout;

    }

    catch(err){

       if(
          pendingTransferRef.current
       ){

          clearTimeout(

             pendingTransferRef
             .current
             ._timeout

          );

          pendingTransferRef.current =
          null;

       }

       reject({

          error:
          "Transfer failed"

       });

    }

  }),
[
   socket,
]

);

  return { transferUSWD };
};

export default useUswdSocket;